import { api } from 'constants/constant';
import { getHeaders } from 'auth/auth';
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

/**
 * Pre-QC — the compliance gate before Clear to Close.
 *
 * Every mutation invalidates the gate as well as the findings, because the
 * four moves a reviewer can make (cure, rebut, sign an exception, return to
 * Underwriting) all change whether the file can close. The backend returns the
 * refreshed gate on each of them, but the tag invalidation keeps the other
 * tabs honest if the user is looking at one of those instead.
 */
const BASE = (loanId) => `/b2b/loan/applications/${loanId}/pre-qc`;

/**
 * The two exports, fetched as blobs rather than opened directly.
 *
 * Both endpoints are cookie-authenticated and stream a file, so neither can be
 * an RTK query and neither can be a bare `window.open`: that carries no
 * credentials and resolves against the frontend origin, landing on the SPA
 * fallback and quietly showing nothing.
 *
 * Same reasoning as `fetchDisclosureDocument`, and deliberately the same shape
 * so there is one download idiom in the app rather than two.
 */
const fetchExport = async (url, accept) => {
  const headers = getHeaders();
  delete headers['Content-Type'];
  headers.Accept = accept;

  const response = await fetch(url, { method: 'GET', credentials: 'include', headers });

  if (!response.ok) {
    // The server sends a JSON envelope on failure, not a file. Read the reason
    // rather than handing the user a corrupt download.
    let detail = `The server returned ${response.status}.`;
    try {
      const body = await response.json();
      if (body?.detail) detail = body.detail;
    } catch {
      /* not JSON — keep the status line */
    }
    throw new Error(detail);
  }

  return response.blob();
};

export const fetchPreQcReport = (loanId) =>
  fetchExport(`${api}${BASE(loanId)}/report.pdf`, 'application/pdf');

/**
 * Findings as CSV. Takes the same filters as the list so the export matches
 * what the reviewer is looking at rather than silently returning everything.
 */
export const fetchPreQcFindingsCsv = (loanId, { family, severity, status } = {}) => {
  const params = new URLSearchParams();
  if (family && family !== 'all') params.set('family', family);
  if (severity && severity !== 'all') params.set('severity', severity);
  if (status && status !== 'all') params.set('status', status);
  const qs = params.toString();
  return fetchExport(`${api}${BASE(loanId)}/findings.csv${qs ? `?${qs}` : ''}`, 'text/csv');
};


export const preQcAPI = createApi({
  reducerPath: 'preQc',
  tagTypes: ['PreQcGate', 'PreQcFindings', 'PreQcRuns'],
  baseQuery: fetchBaseQuery({ baseUrl: api, credentials: 'include' }),
  endpoints: (builder) => ({
    getGate: builder.query({
      query: ({ loanId }) => ({
        url: `${BASE(loanId)}/gate`,
        method: 'GET',
        headers: getHeaders(),
      }),
      transformResponse: (response) => response?.data ?? null,
      providesTags: ['PreQcGate'],
    }),

    getFindings: builder.query({
      query: ({ loanId, family, severity, status }) => {
        const params = new URLSearchParams();
        if (family) params.set('family', family);
        if (severity) params.set('severity', severity);
        if (status) params.set('status', status);
        const qs = params.toString();
        return {
          url: `${BASE(loanId)}/findings${qs ? `?${qs}` : ''}`,
          method: 'GET',
          headers: getHeaders(),
        };
      },
      transformResponse: (response) => response?.data ?? [],
      providesTags: ['PreQcFindings'],
    }),

    getRuns: builder.query({
      query: ({ loanId }) => ({
        url: `${BASE(loanId)}/runs`,
        method: 'GET',
        headers: getHeaders(),
      }),
      transformResponse: (response) => response?.data ?? [],
      providesTags: ['PreQcRuns'],
    }),

    createRun: builder.mutation({
      query: ({ loanId, trigger = 'advisory' }) => ({
        url: `${BASE(loanId)}/runs`,
        method: 'POST',
        headers: getHeaders(),
        body: { trigger },
      }),
      transformResponse: (response) => response?.data ?? null,
      invalidatesTags: ['PreQcGate', 'PreQcFindings', 'PreQcRuns'],
    }),

    resolveFinding: builder.mutation({
      query: ({ loanId, findingId, status, note }) => ({
        url: `${BASE(loanId)}/findings/${findingId}/resolve`,
        method: 'POST',
        headers: getHeaders(),
        body: { status, note },
      }),
      transformResponse: (response) => response?.data ?? null,
      invalidatesTags: ['PreQcGate', 'PreQcFindings'],
    }),

    signException: builder.mutation({
      query: ({ loanId, findingId, justification }) => ({
        url: `${BASE(loanId)}/findings/${findingId}/exception`,
        method: 'POST',
        headers: getHeaders(),
        body: { justification },
      }),
      transformResponse: (response) => response?.data ?? null,
      invalidatesTags: ['PreQcGate', 'PreQcFindings'],
    }),

    returnToUnderwriting: builder.mutation({
      query: ({ loanId, findingId, note = '' }) => ({
        url: `${BASE(loanId)}/findings/${findingId}/return-to-underwriting`,
        method: 'POST',
        headers: getHeaders(),
        body: { note },
      }),
      transformResponse: (response) => response?.data ?? null,
      invalidatesTags: ['PreQcGate', 'PreQcFindings'],
    }),

    getDataDocs: builder.query({
      query: ({ loanId }) => ({
        url: `${BASE(loanId)}/data-docs`,
        method: 'GET',
        headers: getHeaders(),
      }),
      transformResponse: (response) => response?.data ?? null,
      providesTags: ['PreQcGate'],
    }),

    getClearToClose: builder.query({
      query: ({ loanId }) => ({
        url: `${BASE(loanId)}/clear-to-close`,
        method: 'GET',
        headers: getHeaders(),
      }),
      transformResponse: (response) => response?.data ?? null,
      providesTags: ['PreQcGate'],
    }),

    /**
     * Release Clear to Close.
     *
     * Invalidates the runs as well as the gate: the release is stamped onto the
     * run it was granted against, so Run History is stale the moment this
     * succeeds. The server recomputes the decision before honouring it, so a
     * 409 here is the gate refusing on current facts rather than a failed
     * request — and its detail is the sentence worth showing.
     */
    releaseClearToClose: builder.mutation({
      query: ({ loanId }) => ({
        url: `${BASE(loanId)}/clear-to-close`,
        method: 'POST',
        headers: getHeaders(),
      }),
      transformResponse: (response) => response?.data ?? null,
      invalidatesTags: ['PreQcGate', 'PreQcFindings', 'PreQcRuns'],
    }),
  }),
});

export const {
  useGetGateQuery,
  useGetFindingsQuery,
  useGetRunsQuery,
  useGetDataDocsQuery,
  useCreateRunMutation,
  useResolveFindingMutation,
  useSignExceptionMutation,
  useReturnToUnderwritingMutation,
  useGetClearToCloseQuery,
  useReleaseClearToCloseMutation,
} = preQcAPI;
